import type { IBrandingEntity, IBrandingProperty } from '@interfaces/map';
import type { IBrandingEntityValues } from '@interfaces/rooms';

import {
  type IBrandingEntityCatalogueEntry,
  toStoredBrandingValue,
  validateBrandingPropertyValue,
} from './brandingEntities';

/**
 * An entity as the CSV sees it.
 *
 * `defaultSource` is the only thing the file needs beyond the entity itself, and
 * it is optional so a plain `IBrandingEntity` — everything the map declares —
 * still passes: those are all level-authored, which is what its absence means.
 */
export type IBrandingCsvEntity = IBrandingEntity &
  Partial<Pick<IBrandingEntityCatalogueEntry, 'defaultSource'>>;

/**
 * Columns of the objects CSV, in the order they are written.
 *
 * Only `entityId`, `propertyId` and `value` are read back on import — the rest
 * are there so the exported file doubles as a fill-in template, and an editor
 * can see what a property accepts without opening the hub. `value` comes right
 * after the property it belongs to: it is the one column an import reads, and
 * putting it last left it past a run of filled-in reference cells that invite
 * an edit the import then ignores.
 *
 * `defaultValue` is paired with `defaultSource` because the two answer different
 * questions: what the value falls back to, and whether that fallback was
 * authored on the placed actor in its level or ships with the class. A row for
 * an object that only exists at runtime can only ever carry the class one, and
 * a file mixes both kinds — this is the only place outside the hub's own UI
 * where the reader can tell them apart.
 */
export const BRANDING_ENTITIES_CSV_COLUMNS = [
  'entityId',
  'entityName',
  'propertyId',
  'propertyName',
  'value',
  'type',
  'allowedValues',
  'defaultValue',
  'defaultSource',
] as const;

/** Columns an imported file must carry; anything else in the header is ignored. */
const REQUIRED_COLUMNS = ['entityId', 'propertyId', 'value'] as const;

/**
 * Columns the import reads past. Each is derived from the build's catalogue, so
 * a file that disagrees with the catalogue has been hand-edited in a column
 * that changes nothing — the likeliest reason an import appears to do nothing.
 */
const REFERENCE_COLUMNS = [
  'entityName',
  'propertyName',
  'type',
  'allowedValues',
  'defaultValue',
  'defaultSource',
] as const;

/**
 * Column names earlier exports used, mapped onto the ones in use.
 *
 * `levelDefault` was the header before runtime objects could appear in the file,
 * when every default really did come from a level. Recognising it keeps a file
 * exported back then reporting its reference edits, which is the only thing an
 * unrecognised column would silently stop doing — an import of one has always
 * worked and still does.
 */
const LEGACY_COLUMN_NAMES: Record<string, string> = {
  levelDefault: 'defaultValue',
};

export type BrandingCsvReferenceColumn = (typeof REFERENCE_COLUMNS)[number];

/**
 * Suffix the export appends to every reference column's header.
 *
 * The header is the only place a spreadsheet shows what a column is for, and
 * nothing else about a reference cell says it cannot be edited — it holds a
 * colour or a size, exactly what the editor came to change. Headers are matched
 * by name with this suffix stripped, so a file exported before it existed, or
 * one written by hand, still imports.
 */
export const READ_ONLY_HEADER_SUFFIX = ' (read-only)';

/**
 * Renders the header cell for a column.
 *
 * @param column - Logical column name.
 * @returns The header as written to the file.
 */
export const toCsvHeaderCell = (column: string): string =>
  (REFERENCE_COLUMNS as readonly string[]).includes(column)
    ? `${column}${READ_ONLY_HEADER_SUFFIX}`
    : column;

/**
 * Reduces a header cell to the column it names.
 *
 * @param cell - Header cell as read from the file.
 * @returns The logical column name.
 */
export const fromCsvHeaderCell = (cell: string): string => {
  const trimmed = cell.trim();
  const name = trimmed.endsWith(READ_ONLY_HEADER_SUFFIX)
    ? trimmed.slice(0, -READ_ONLY_HEADER_SUFFIX.length).trim()
    : trimmed;

  return LEGACY_COLUMN_NAMES[name] ?? name;
};

/**
 * Renders the `defaultSource` cell for an entity.
 *
 * @param entity - Catalogue entity the row belongs to.
 * @returns `class` for an object that only exists at runtime, `level` otherwise.
 */
const toDefaultSourceCell = (entity: IBrandingCsvEntity): string =>
  entity.defaultSource === 'class' ? 'class' : 'level';

/** Separator inside the informational `allowedValues` cell. */
const ALLOWED_VALUES_SEPARATOR = ' | ';

// Excel reads a CSV as the system's legacy code page unless the file opens with
// a UTF-8 BOM, which mangles every non-ASCII display name we export.
const UTF8_BOM = '﻿';

const CRLF = '\r\n';

/** Why one row of an imported file cannot be applied. */
export type BrandingCsvSkipReason =
  | 'malformedRow'
  | 'unknownEntity'
  | 'unknownProperty'
  | 'invalidValue'
  | 'duplicateRow';

/** Why a whole file is unusable, as opposed to one of its rows. */
export type BrandingCsvFileError = 'empty' | 'missingColumns';

export interface IBrandingCsvSkippedRow {
  /** 1-based line in the file, counting the header, so it matches an editor. */
  line: number;
  reason: BrandingCsvSkipReason;
  entityId: string;
  propertyId: string;
}

export interface IBrandingCsvReferenceEdit {
  /** 1-based line in the file, counting the header, so it matches an editor. */
  line: number;
  entityId: string;
  propertyId: string;
  /** Reference columns of that row the file disagrees with the catalogue on. */
  columns: BrandingCsvReferenceColumn[];
}

export interface IBrandingCsvChange {
  entityId: string;
  propertyId: string;
  /** Value stored today; `''` means the property is not overridden. */
  from: string;
  /** Value after the import; `''` means the override is cleared. */
  to: string;
}

export interface IBrandingCsvImportPlan {
  /**
   * Complete property sets of the entities the file touches, ready to be sent
   * as one PATCH body. Entities the file never names are absent, so the
   * backend's per-entity replace leaves them alone.
   */
  entities: IBrandingEntityValues;
  /**
   * Every value this import changes, old and new. The confirmation is only
   * meaningful if it shows what is about to happen, not just how much.
   */
  changes: IBrandingCsvChange[];
  /** `changes.length`, kept for the summary line. */
  appliedCount: number;
  /** Entities carrying at least one changed value. */
  changedEntityIds: string[];
  skipped: IBrandingCsvSkippedRow[];
  /**
   * Rows whose reference cells disagree with the catalogue, i.e. rows edited in
   * a column the import reads past. Advisory: nothing is refused over it, but it
   * is what explains an import that changes less than the file looks like it
   * should — so it carries the line, like a skipped row does.
   */
  referenceEdits: IBrandingCsvReferenceEdit[];
  /**
   * Absolute asset URLs pointing at another environment. Advisory only — they
   * are imported as given, they just will not resolve.
   */
  foreignAssetUrls: string[];
  /** Set when the file as a whole cannot be read; `entities` is then empty. */
  fileError: BrandingCsvFileError | null;
}

/**
 * Quotes one CSV cell when its content would otherwise break the row.
 *
 * @param cell - Raw cell text.
 * @returns The cell as it is written to the file.
 */
const escapeCsvCell = (cell: string): string => {
  const needsQuotes = /[",\r\n]/.test(cell) || cell !== cell.trim();

  if (!needsQuotes) {
    return cell;
  }

  return `"${cell.replace(/"/g, '""')}"`;
};

/**
 * Renders a grid of cells as RFC 4180 text.
 *
 * @param rows - Cells, outer array being the rows.
 * @returns The file content, without the byte-order mark.
 */
export const serializeCsvRows = (rows: string[][]): string =>
  rows.map((row) => row.map(escapeCsvCell).join(',')).join(CRLF);

/**
 * Reads RFC 4180 text back into a grid of cells.
 *
 * Everything comes back as a string: a spreadsheet may well write `true` or
 * `007`, and coercing either would change the value Unreal receives.
 *
 * @param text - File content, with or without a byte-order mark.
 * @returns The rows, with trailing blank lines dropped.
 */
export const parseCsvRows = (text: string): string[][] => {
  const source = text.startsWith(UTF8_BOM) ? text.slice(UTF8_BOM.length) : text;

  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let isQuoted = false;
  let index = 0;

  const endCell = () => {
    row.push(cell);
    cell = '';
  };

  const endRow = () => {
    endCell();
    rows.push(row);
    row = [];
  };

  while (index < source.length) {
    const char = source[index];

    if (isQuoted) {
      if (char === '"') {
        if (source[index + 1] === '"') {
          cell += '"';
          index += 2;
          continue;
        }

        isQuoted = false;
        index += 1;
        continue;
      }

      cell += char;
      index += 1;
      continue;
    }

    if (char === '"' && cell === '') {
      isQuoted = true;
      index += 1;
      continue;
    }

    if (char === ',') {
      endCell();
      index += 1;
      continue;
    }

    if (char === '\r' || char === '\n') {
      endRow();
      // A CRLF pair ends one row, not two.
      index += char === '\r' && source[index + 1] === '\n' ? 2 : 1;
      continue;
    }

    cell += char;
    index += 1;
  }

  if (cell !== '' || row.length > 0) {
    endRow();
  }

  // A file ending in a newline yields one empty trailing row; so does a blank
  // line an editor left behind.
  return rows.filter((candidate) => candidate.some((value) => value !== ''));
};

/**
 * Writes the whole catalogue out, overridden values included.
 *
 * Every declared property gets a row, so an empty `value` is a property the
 * configuration does not override and the file can be filled in by hand.
 *
 * @param catalogue - Brandable entities the active build declares.
 * @param values - Values this configuration stores.
 * @returns The file content, byte-order mark included.
 */
export const buildBrandingEntitiesCsv = (
  catalogue: IBrandingCsvEntity[],
  values: IBrandingEntityValues,
): string => {
  const rows: string[][] = [BRANDING_ENTITIES_CSV_COLUMNS.map(toCsvHeaderCell)];

  catalogue.forEach((entity) => {
    const entityValues = values[entity.entityId] ?? {};

    entity.properties.forEach((property) => {
      rows.push([
        entity.entityId,
        entity.displayName ?? '',
        property.propertyId,
        property.displayName ?? '',
        entityValues[property.propertyId] ?? '',
        property.type ?? '',
        (property.allowedValues ?? []).join(ALLOWED_VALUES_SEPARATOR),
        property.default ?? '',
        toDefaultSourceCell(entity),
      ]);
    });
  });

  return `${UTF8_BOM}${serializeCsvRows(rows)}`;
};

/**
 * Resolves the host suffix that marks an asset URL as belonging to this
 * environment.
 *
 * Asset hosts are not configured in the hub — only the API is — but every
 * environment publishes both under the same parent domain
 * (`api.dev.example.com` alongside `assets.dev.example.com`), so dropping the
 * first label of the API host gives a suffix that matches this environment's
 * assets and nothing from another one.
 *
 * @param apiUrl - `REACT_APP_API_URL` of the running hub.
 * @returns The suffix to test hostnames against, or `null` when it cannot be derived.
 */
export const resolveEnvironmentHostSuffix = (
  apiUrl: string | undefined,
): string | null => {
  if (!apiUrl) {
    return null;
  }

  try {
    const { hostname } = new URL(apiUrl);
    const labels = hostname.split('.');

    // Nothing to drop on a bare host (`localhost`), and a two-label host is
    // already the parent domain.
    if (labels.length <= 2) {
      return hostname;
    }

    return labels.slice(1).join('.');
  } catch (parseError) {
    return null;
  }
};

/**
 * Finds imported values that point at another environment's assets.
 *
 * The backend relocates only what still sits under the temporary `uploads/`
 * prefix, so any other absolute URL is stored verbatim — an asset URL carried
 * over from a different environment stays pointing there and will not resolve.
 *
 * @param entities - Values the import would store.
 * @param apiUrl - `REACT_APP_API_URL` of the running hub.
 * @returns The offending URLs, deduplicated and in the order first seen.
 */
export const collectForeignAssetUrls = (
  entities: IBrandingEntityValues,
  apiUrl: string | undefined,
): string[] => {
  const suffix = resolveEnvironmentHostSuffix(apiUrl);

  // Without a reference host every URL would look foreign, and a warning on
  // every row is worse than none.
  if (!suffix) {
    return [];
  }

  const foreign: string[] = [];

  Object.values(entities).forEach((properties) => {
    Object.values(properties).forEach((value) => {
      if (!/^https?:\/\//i.test(value) || foreign.includes(value)) {
        return;
      }

      try {
        const { hostname } = new URL(value);

        if (hostname !== suffix && !hostname.endsWith(`.${suffix}`)) {
          foreign.push(value);
        }
      } catch (parseError) {
        // A value that only looks like a URL is left to Unreal to reject.
      }
    });
  });

  return foreign;
};

interface IIndexedEntity {
  entity: IBrandingCsvEntity;
  properties: Map<string, IBrandingProperty>;
}

/**
 * Indexes the catalogue so a row can be resolved without rescanning it.
 *
 * @param catalogue - Brandable entities the active build declares.
 * @returns The entity and its properties by property id, keyed by entity id.
 */
const indexCatalogue = (
  catalogue: IBrandingCsvEntity[],
): Map<string, IIndexedEntity> => {
  const index = new Map<string, IIndexedEntity>();

  catalogue.forEach((entity) => {
    index.set(entity.entityId, {
      entity,
      properties: new Map(
        entity.properties.map((property) => [property.propertyId, property]),
      ),
    });
  });

  return index;
};

/**
 * Renders what a reference column holds for a property, as the export writes it.
 *
 * @param column - Reference column being read.
 * @param entity - Catalogue entity the row resolved to.
 * @param property - Catalogue property the row resolved to.
 * @returns The cell the export would have written.
 */
const getReferenceCellValue = (
  column: BrandingCsvReferenceColumn,
  entity: IBrandingCsvEntity,
  property: IBrandingProperty,
): string => {
  switch (column) {
    case 'entityName':
      return entity.displayName ?? '';
    case 'propertyName':
      return property.displayName ?? '';
    case 'type':
      return property.type ?? '';
    case 'allowedValues':
      return (property.allowedValues ?? []).join(ALLOWED_VALUES_SEPARATOR);
    case 'defaultSource':
      return toDefaultSourceCell(entity);
    default:
      return property.default ?? '';
  }
};

/**
 * Turns an exported file back into the values to store.
 *
 * The file only ever adds to what a configuration holds: an entity it names is
 * merged onto that entity's stored values, so a row the file omits keeps the
 * value it had. An empty `value` is the one exception — it clears the property,
 * which is what restores what the level authored.
 *
 * @param text - File content as read from disk.
 * @param catalogue - Brandable entities the active build declares.
 * @param storedValues - Values this configuration holds today.
 * @param apiUrl - `REACT_APP_API_URL`, used to spot another environment's assets.
 * @returns What the import would do, for the user to confirm.
 */
export const parseBrandingEntitiesCsv = (
  text: string,
  catalogue: IBrandingCsvEntity[],
  storedValues: IBrandingEntityValues,
  apiUrl?: string,
): IBrandingCsvImportPlan => {
  const emptyPlan: IBrandingCsvImportPlan = {
    entities: {},
    changes: [],
    appliedCount: 0,
    changedEntityIds: [],
    skipped: [],
    referenceEdits: [],
    foreignAssetUrls: [],
    fileError: null,
  };

  const rows = parseCsvRows(text);

  if (rows.length === 0) {
    return { ...emptyPlan, fileError: 'empty' };
  }

  const header = rows[0].map(fromCsvHeaderCell);
  const columnIndex = new Map(header.map((name, index) => [name, index]));

  if (REQUIRED_COLUMNS.some((column) => !columnIndex.has(column))) {
    return { ...emptyPlan, fileError: 'missingColumns' };
  }

  const entityIdIndex = columnIndex.get('entityId') as number;
  const propertyIdIndex = columnIndex.get('propertyId') as number;
  const valueIndex = columnIndex.get('value') as number;

  const index = indexCatalogue(catalogue);
  const skipped: IBrandingCsvSkippedRow[] = [];
  const referenceEdits: IBrandingCsvReferenceEdit[] = [];
  // Values being built up per entity, seeded from what is already stored so an
  // untouched property survives the per-entity replace.
  const merged = new Map<string, Record<string, string>>();
  // Where each pair was last accepted, so a repeat can retract the earlier row.
  const acceptedAt = new Map<string, number>();

  rows.slice(1).forEach((row, rowIndex) => {
    // Header is line 1, so the first data row is line 2.
    const line = rowIndex + 2;
    const entityId = (row[entityIdIndex] ?? '').trim();
    const propertyId = (row[propertyIdIndex] ?? '').trim();
    const rawValue = row[valueIndex] ?? '';

    if (!entityId || !propertyId) {
      skipped.push({ line, reason: 'malformedRow', entityId, propertyId });
      return;
    }

    const indexed = index.get(entityId);

    if (!indexed) {
      skipped.push({ line, reason: 'unknownEntity', entityId, propertyId });
      return;
    }

    const property = indexed.properties.get(propertyId);

    if (!property) {
      skipped.push({ line, reason: 'unknownProperty', entityId, propertyId });
      return;
    }

    // Collected before the value is judged: a row can carry both a reference
    // edit and an unusable value, and the reference edit is the more useful
    // thing to report of the two.
    const editedColumns = REFERENCE_COLUMNS.filter((column) => {
      const cellIndex = columnIndex.get(column);

      if (cellIndex === undefined) {
        return false;
      }

      const cell = (row[cellIndex] ?? '').trim();

      // An empty reference cell is a file that was written by hand, not one
      // whose reference column was edited — only a filled-in disagreement is
      // evidence of the mistake this warns about.
      if (!cell) {
        return false;
      }

      return cell !== getReferenceCellValue(column, indexed.entity, property);
    });

    if (editedColumns.length > 0) {
      referenceEdits.push({
        line,
        entityId,
        propertyId,
        columns: editedColumns,
      });
    }

    if (validateBrandingPropertyValue(property, rawValue)) {
      skipped.push({ line, reason: 'invalidValue', entityId, propertyId });
      return;
    }

    const pairKey = `${entityId} ${propertyId}`;
    const previousLine = acceptedAt.get(pairKey);

    if (previousLine !== undefined) {
      // The later row wins, so the earlier one is reported as dropped rather
      // than silently overwritten.
      skipped.push({
        line: previousLine,
        reason: 'duplicateRow',
        entityId,
        propertyId,
      });
    }

    acceptedAt.set(pairKey, line);

    if (!merged.has(entityId)) {
      merged.set(entityId, { ...(storedValues[entityId] ?? {}) });
    }

    const entityValues = merged.get(entityId) as Record<string, string>;
    const nextValue = toStoredBrandingValue(property, rawValue);

    if (nextValue) {
      entityValues[propertyId] = nextValue;
    } else {
      delete entityValues[propertyId];
    }
  });

  // Counted from the finished sets rather than row by row: a pair the file
  // repeats, or sets back to what is already stored, must not inflate the
  // number the confirmation shows.
  const entities: IBrandingEntityValues = {};
  const changedEntityIds: string[] = [];
  const changes: IBrandingCsvChange[] = [];

  merged.forEach((properties, entityId) => {
    entities[entityId] = properties;

    const stored = storedValues[entityId] ?? {};
    const propertyIds = new Set([
      ...Object.keys(stored),
      ...Object.keys(properties),
    ]);
    let changedHere = 0;

    propertyIds.forEach((propertyId) => {
      const from = stored[propertyId] ?? '';
      const to = properties[propertyId] ?? '';

      if (from !== to) {
        changes.push({ entityId, propertyId, from, to });
        changedHere += 1;
      }
    });

    if (changedHere > 0) {
      changedEntityIds.push(entityId);
    }
  });

  // Sorted rather than left in traversal order, which follows the stored keys
  // before the file's: the report is read against the file, so it has to come
  // out the same way whichever configuration it is compared to.
  changes.sort(
    (left, right) =>
      left.entityId.localeCompare(right.entityId) ||
      left.propertyId.localeCompare(right.propertyId),
  );

  return {
    entities,
    changes,
    appliedCount: changes.length,
    changedEntityIds,
    skipped,
    referenceEdits,
    foreignAssetUrls: collectForeignAssetUrls(entities, apiUrl),
    fileError: null,
  };
};
